import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { unlockAudio } from "@/lib/audioUnlock";
import { Play, Clock, Check, Users, Lock } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import MorningContent from "@/pages/Morning";

/**
 * Shared session-entry screen — used by both /rest (mode=sleep) and
 * /adventure (mode=roleplay). The mode toggle at the top is a TRUE
 * segmented control: the labels [Rest][Adventure] are FIXED in place; only
 * the highlight pill animates between them.
 */
const SLIDER_MIN = 5;
const SLIDER_MAX = 90;
const SLIDER_STEP = 1;

// Continua 1.1 — tier defaults (fallbacks only; the live entitlement comes
// from GET /api/subscription and is enforced server-side too).
const FREE_MAX_DURATION = 15;
const FREE_COMPANIONS = ["elizabeth", "seriny"];
const FREE_ATMOSPHERES = ["cozy_cabin", "seaside_evening"];

export const SessionEntryScreen = ({ mode, title, testidPrefix }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  // Iter 121 — unified Sessions screen: the top selector switches between the
  // Sleep entry experience and the Morning configuration experience. The tab
  // lives in the URL (?mode=morning) so old /morning deep links keep working.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("mode") === "morning" ? "morning" : "sleep";
  const setTab = (t) =>
    setSearchParams(t === "morning" ? { mode: "morning" } : {}, { replace: true });
  const [duration, setDuration] = useState(15);
  const [atmospheres, setAtmospheres] = useState([]);
  const [atmoId, setAtmoId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [sub, setSub] = useState(null); // Continua 1.1 — live entitlement
  const isPremium = !!user?.is_premium;
  const sliderMax = sub?.max_duration_minutes ?? (isPremium ? SLIDER_MAX : FREE_MAX_DURATION);
  const allowedAtmospheres = sub?.allowed_atmospheres ?? (isPremium ? null : FREE_ATMOSPHERES);
  const allowedCompanions = sub?.allowed_companions ?? FREE_COMPANIONS;
  const atmoAllowed = (id) => id === "custom" || !allowedAtmospheres || allowedAtmospheres.includes(id);

  useEffect(() => {
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => setSub(null));
  }, [user?.subscription_tier]);

  useEffect(() => {
    if (!user) return;
    let preferred = user.atmosphere_id || (user.custom_atmosphere ? "custom" : "forest_campfire");
    // Iter 115 — preserve configuration state across navigation.
    try {
      const saved = JSON.parse(localStorage.getItem(`continua_config_${mode}`) || "null");
      if (saved?.atmoId) preferred = saved.atmoId;
      if (saved?.duration) setDuration(Math.min(Number(saved.duration) || 15, sliderMax));
    } catch (e) { /* ignore */ }
    if (!atmoAllowed(preferred)) {
      setAtmoId((allowedAtmospheres || FREE_ATMOSPHERES)[0]);
    } else {
      setAtmoId(preferred);
    }
  }, [user, isPremium, sub]);

  useEffect(() => {
    if (!atmoId) return;
    try { localStorage.setItem(`continua_config_${mode}`, JSON.stringify({ atmoId, duration })); } catch (e) { /* ignore */ }
  }, [atmoId, duration, mode]);

  useEffect(() => {
    api.get("/catalog/atmospheres").then((r) => setAtmospheres(r.data || []));
  }, []);

  const startSession = async () => {
    if (!user) return;
    if (!user.character_id) { nav("/characters"); return; }
    unlockAudio(); // real user gesture — arm audio for the session screens
    if (!allowedCompanions.includes(user.character_id)) {
      toast("This companion isn't available on your plan.", {
        action: { label: "Companions", onClick: () => nav("/characters") },
      });
      return;
    }
    setStarting(true);
    const selectedAtmo = atmospheres.find((a) => a.id === atmoId);
    nav("/waiting", {
      state: {
        launchId: Date.now(), // Iter 86 — fresh id starts a NEW hosted session
        mode,
        durationMinutes: Math.min(duration, sliderMax),
        atmosphereId: atmoId || "forest_campfire",
        atmoName: selectedAtmo?.name || user.atmosphere_name || "the quiet place",
        atmoImage: selectedAtmo?.image,
        characterId: user.character_id,
        generateMorning: !!user.morning_enabled,
        customAtmosphere: atmoId === "custom" ? user.custom_atmosphere : undefined,
      },
    });
  };

  // Iter 121 — true segmented control: [Sleep][Morning] labels are fixed in
  // place; only the highlight pill translates between them.
  const pillTransform = tab === "morning" ? "translateX(100%)" : "translateX(0%)";

  return (
    <AppShell>
      <div
        className="max-w-md mx-auto px-6 pt-2 pb-12"
        data-testid={`${testidPrefix}-screen`}
      >
        {/* Sleep / Morning selector — both modes live inside Sessions. */}
        <div
          className="relative w-[220px] p-1 rounded-full glass mb-4 select-none"
          data-testid={`${testidPrefix}-mode-toggle`}
        >
          <div className="relative grid grid-cols-2">
            <span
              aria-hidden="true"
              className="absolute inset-y-0 left-0 w-1/2 rounded-full bg-violet-200/15 border border-violet-200/35 transition-transform duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)] pointer-events-none"
              style={{ transform: pillTransform }}
              data-testid={`${testidPrefix}-mode-pill`}
            />
            <button
              type="button"
              onClick={() => setTab("sleep")}
              data-testid={`${testidPrefix}-mode-sleep`}
              className={`relative z-10 py-2 rounded-full text-sm text-center transition-colors ${
                tab === "sleep" ? "text-violet-50" : "text-violet-300/55 hover:text-violet-100"
              }`}
            >
              Sleep
            </button>
            <button
              type="button"
              onClick={() => setTab("morning")}
              data-testid={`${testidPrefix}-mode-morning`}
              className={`relative z-10 py-2 rounded-full text-sm text-center transition-colors ${
                tab === "morning" ? "text-violet-50" : "text-violet-300/55 hover:text-violet-100"
              }`}
            >
              Morning
            </button>
          </div>
        </div>

        {tab === "morning" ? (
          <MorningContent onBeginNight={() => setTab("sleep")} />
        ) : (
        <>
        <h1
          className="font-serif-display text-5xl text-violet-50 mb-10 leading-none"
          data-testid={`${testidPrefix}-title`}
        >
          {title}
        </h1>

        {/* Centered Play button */}
        <div className="flex justify-center mb-12">
          <button
            type="button"
            onClick={startSession}
            disabled={starting}
            data-testid={`${testidPrefix}-play-button`}
            aria-label={`Start ${title} session`}
            className="relative group"
          >
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full bg-violet-300/10 blur-2xl group-hover:bg-violet-300/16 transition-colors"
            />
            <span
              className={`relative flex items-center justify-center w-36 h-36 rounded-full border border-violet-200/35 bg-violet-200/8 backdrop-blur-md transition-all ${
                starting ? "opacity-60" : "group-hover:border-violet-100/55 group-hover:bg-violet-200/14 group-active:scale-95"
              }`}
            >
              <Play className="w-10 h-10 text-violet-50 translate-x-[3px]" strokeWidth={1.25} />
            </span>
          </button>
        </div>

        {/* Iter 86 — Character selection is part of configuring a session
            (moved here from beneath the Home Play button).
            Flow: Sleep → Character → Duration → Play. */}
        <section className="mb-10" data-testid={`${testidPrefix}-character-section`}>
          <h2 className="text-xs uppercase tracking-[0.32em] text-violet-300/55 flex items-center gap-1.5 mb-3">
            <Users className="w-3 h-3" strokeWidth={1.5} /> Character
          </h2>
          <button
            type="button"
            onClick={() => nav("/characters")}
            data-testid={`${testidPrefix}-character-button`}
            className="w-full flex items-center justify-between rounded-2xl border border-violet-200/20 bg-violet-200/5 px-5 py-4 text-left hover:border-violet-200/35 hover:bg-violet-200/8 transition-all"
          >
            <span className="text-violet-50/90 text-sm flex items-center gap-2">
              {user?.character_id
                ? user.character_id === "seriny" ? "Serine" : user.character_id === "elizabeth" ? "Elizabeth" : user.character_id
                : "Choose a companion"}
              {user?.character_id && !allowedCompanions.includes(user.character_id) && (
                <span className="text-[9px] uppercase tracking-[0.24em] text-violet-300/45 border border-violet-300/20 rounded-full px-2 py-0.5" data-testid={`${testidPrefix}-character-premium-chip`}>
                  Premium
                </span>
              )}
            </span>
            <span className="text-xs text-violet-300/50">Change</span>
          </button>
        </section>

        {/* Duration — single elegant slider with live value */}
        <section className="mb-10" data-testid={`${testidPrefix}-duration-section`}>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xs uppercase tracking-[0.32em] text-violet-300/55 flex items-center gap-1.5">
              <Clock className="w-3 h-3" strokeWidth={1.5} /> Duration
            </h2>
            <span
              className="font-serif-display text-2xl text-violet-50 tabular-nums"
              data-testid={`${testidPrefix}-duration-value`}
            >
              {duration}<span className="text-sm text-violet-300/55 ml-1">min</span>
            </span>
          </div>
          <input
            type="range"
            min={SLIDER_MIN}
            max={sliderMax}
            step={SLIDER_STEP}
            value={Math.min(duration, sliderMax)}
            onChange={(e) => setDuration(Number(e.target.value))}
            data-testid={`${testidPrefix}-duration-slider`}
            aria-label="Session duration in minutes"
            className="continua-slider w-full"
          />
          <div className="flex justify-between text-[10px] uppercase tracking-[0.28em] text-violet-300/45 mt-1.5">
            <span>{SLIDER_MIN} min</span>
            <span>{sliderMax} min</span>
          </div>
          {sliderMax < SLIDER_MAX && (
            <p className="mt-2 text-[11px] text-violet-300/50" data-testid={`${testidPrefix}-duration-premium-hint`}>
              {sub?.tier === "light"
                ? `Nights up to ${SLIDER_MAX} minutes with Continua Premium.`
                : `Nights up to 20 minutes with Continua Light — up to ${SLIDER_MAX} with Premium.`}{" "}
              <button type="button" onClick={() => nav("/upgrade")} className="underline underline-offset-2 hover:text-violet-200/80 transition-colors" data-testid={`${testidPrefix}-duration-upgrade-link`}>
                View plans
              </button>
            </p>
          )}
        </section>

        {/* Atmosphere list */}
        <section data-testid={`${testidPrefix}-atmosphere-section`}>
          <h2 className="text-xs uppercase tracking-[0.32em] text-violet-300/55 mb-3">
            Atmosphere
          </h2>
          <ul className="space-y-2">
            {atmospheres.map((a) => {
              const selected = atmoId === a.id;
              const locked = !atmoAllowed(a.id);
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (locked) {
                        toast("This atmosphere unlocks with Continua Light.", {
                          action: { label: "View plans", onClick: () => nav("/upgrade") },
                        });
                        return;
                      }
                      setAtmoId(a.id);
                    }}
                    data-testid={`${testidPrefix}-atmosphere-${a.id}`}
                    className={`w-full text-left flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition-colors ${
                      selected
                        ? "border-violet-200/45 bg-violet-200/8 text-violet-50"
                        : locked
                          ? "border-violet-200/8 bg-violet-200/2 text-violet-100/40"
                          : "border-violet-200/12 bg-violet-200/3 text-violet-100/80 hover:bg-violet-200/6"
                    }`}
                  >
                    <span className="font-serif-display text-base flex items-center gap-2">
                      {locked && <Lock className="w-3.5 h-3.5 text-violet-300/40" strokeWidth={1.5} />}
                      {a.name}
                    </span>
                    {selected && <Check className="w-4 h-4 text-violet-200" strokeWidth={1.5} />}
                    {locked && (
                      <span className="text-[9px] uppercase tracking-[0.24em] text-violet-300/45 border border-violet-300/20 rounded-full px-2 py-0.5" data-testid={`${testidPrefix}-atmosphere-${a.id}-premium-chip`}>
                        Light
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
        </>
        )}
      </div>
    </AppShell>
  );
};

export default function Rest() {
  return <SessionEntryScreen mode="sleep" title="Sleep" testidPrefix="rest" />;
}
